import { useState } from 'react';
import { useParams, Link, Navigate } from 'react-router-dom';
import SEO from '../components/SEO';
import QuoteSection from '../components/QuoteSection';
import LightboxModal from '../components/LightboxModal';
import { useProjects, useCompanyInfo } from '../lib/cms';
import { imageUrl } from '../lib/images';

export default function ProjektDetailPage() {
  const { slug } = useParams();
  const [lightboxIndex, setLightboxIndex] = useState(null);
  const projects = useProjects();
  const companyInfo = useCompanyInfo();

  // find match either by slug or with "nyheder-" prefix
  const project = projects.find(
    (p) => p.slug === slug || p.slug === `nyheder-${slug}` || p.path === `/nyheder/${slug}`
  );

  if (!project) {
    return <Navigate to="/projekter" replace />;
  }

  return (
    <>
      <SEO
        title={project.metaTitle || `${project.title} | Hovendals`}
        description={project.metaDesc}
        ogImage={project.mainImage}
      />

      {/* Hero */}
      <section className="relative py-20 bg-slate-900 text-white overflow-hidden">
        <div className="absolute inset-0 z-0 opacity-25">
          <img
            src={imageUrl(project.mainImage, "width=1600,quality=80,format=auto")}
            alt={project.title}
            className="w-full h-full object-cover"
          />
        </div>
        <div className="max-w-[1440px] mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="max-w-3xl space-y-4">
            <div className="flex items-center gap-3">
              <Link
                to="/projekter"
                className="inline-flex items-center gap-1.5 text-xs font-semibold text-[#c2ccdf] hover:text-[#c2ccdf]"
              >
                <i className="fa-sharp fa-solid fa-arrow-left text-xs" aria-hidden="true"></i>
                <span>Alle projekter</span>
              </Link>
              <span className="text-slate-600">/</span>
              <span className="px-2.5 py-0.5 rounded-full text-xs font-bold uppercase tracking-wider bg-slate-800 text-[#c2ccdf] border border-slate-700">
                {project.category}
              </span>
            </div>

            <h1 className="text-3xl sm:text-4xl lg:text-5xl font-extrabold tracking-tight text-white leading-tight">
              {project.title}
            </h1>
          </div>
        </div>
      </section>

      {/* Detail Content */}
      <section className="py-16 md:py-24 bg-white">
        <div className="max-w-[1440px] mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">
            {/* Left Main Content */}
            <div className="lg:col-span-8 space-y-8">
              {/* Main Image */}
              <div
                onClick={() => setSelectedImage(project.mainImage)}
                className="relative rounded-2xl overflow-hidden shadow-lg border border-slate-200 cursor-pointer group bg-slate-100"
              >
                <img
                  src={imageUrl(project.mainImage, "width=1200,quality=85,format=auto")}
                  alt={project.title}
                  className="w-full h-auto max-h-[500px] object-cover transition-transform duration-300 group-hover:scale-102"
                />
                <div className="absolute inset-0 bg-slate-950/30 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center text-white">
                  <span className="p-3 rounded-full bg-[#143463]/90 text-white">
                    <i className="fa-sharp fa-solid fa-magnifying-glass-plus text-lg" aria-hidden="true"></i>
                  </span>
                </div>
              </div>

              {/* Specs & Description */}
              <div className="p-8 rounded-2xl bg-slate-50 border border-slate-200 space-y-4">
                <h2 className="text-xl font-bold text-slate-900">
                  Opgavebeskrivelse & specifikationer
                </h2>

                {/* Specs & Description */}
                {project.specs && (
                  <div className="space-y-2">
                    <h3 className="text-sm font-bold text-slate-700 uppercase tracking-wider">
                      Specifikationer:
                    </h3>
                    <ul className="space-y-1.5 text-sm text-slate-700">
                      {project.specs.map((spec, idx) => (
                        <li key={idx} className="flex items-start gap-2">
                          <i className="fa-sharp fa-solid fa-check text-[#004fa4] mt-1" aria-hidden="true"></i>
                          <span>{spec}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                {/* Show prose description only when it has unique content beyond repeating specs */}
                {(() => {
                  if (!project.description) return null;
                  const trimmedDesc = project.description.trim();
                  // Check if description is just repeating the spec bullets
                  const isJustSpecs = trimmedDesc.startsWith("Specifikationer:") && 
                    project.specs && 
                    project.specs.every(s => trimmedDesc.includes(s.trim()));
                  if (isJustSpecs) return null;

                  return (
                    <div className="pt-4 text-slate-700 text-base leading-relaxed whitespace-pre-line border-t border-slate-200">
                      {project.description}
                    </div>
                  );
                })()}
              </div>

              {/* Gallery of extra images if any */}
              {project.gallery && project.gallery.length > 1 && (
                <div className="space-y-4">
                  <h3 className="text-xl font-bold text-slate-900">
                    Billeder fra projektet
                  </h3>
                  <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
                    {project.gallery.map((img, gIdx) => (
                      <div
                        key={gIdx}
                        onClick={() => setLightboxIndex(gIdx)}
                        className="relative h-44 rounded-xl overflow-hidden bg-slate-100 cursor-pointer shadow-sm hover:shadow-md group"
                      >
                        <img
                          src={imageUrl(img, "width=500,quality=80,format=auto")}
                          alt={`${project.title} - foto ${gIdx + 1}`}
                          className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-105"
                          loading="lazy"
                        />
                        <div className="absolute inset-0 bg-slate-950/40 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center text-white">
                          <i className="fa-sharp fa-solid fa-magnifying-glass-plus text-[#c2ccdf]" aria-hidden="true"></i>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* Right Sidebar */}
            <div className="lg:col-span-4 space-y-6">
              <div className="p-6 rounded-2xl bg-white border border-slate-200 shadow-sm space-y-4">
                <h3 className="font-bold text-slate-900 text-lg">
                  Ønsker du en lignende løsning?
                </h3>
                <p className="text-sm text-slate-600 leading-relaxed">
                  Hos Hovendals hjælper vi dig med at skræddersy dit projekt fra start til slut. Vi måler op, fremstiller på eget værksted og monterer professionelt.
                </p>

                <div className="space-y-3 pt-2">
                  <Link
                    to="/kontakt"
                    className="block w-full text-center py-3 px-4 rounded-lg bg-[#004fa4] hover:bg-[#183e7f] text-white font-medium text-sm shadow-sm transition-all"
                  >
                    Få et uforpligtende tilbud
                  </Link>
                  <a
                    href={`tel:${companyInfo.phoneFormatted}`}
                    className="block w-full text-center py-3 px-4 rounded-lg bg-slate-900 hover:bg-slate-800 text-white font-bold text-sm transition-all"
                  >
                    Ring: {companyInfo.phone}
                  </a>
                </div>
              </div>

              {/* Other projects list */}
              <div className="p-6 rounded-2xl bg-slate-50 border border-slate-200 space-y-4">
                <h4 className="font-bold text-slate-900 text-sm uppercase tracking-wider">
                  Andre projekter
                </h4>
                <div className="space-y-3">
                  {projects
                    .filter((p) => p.slug !== project.slug)
                    .slice(0, 4)
                    .map((item) => (
                      <Link
                        key={item.slug}
                        to={item.path}
                        className="flex items-center gap-3 group p-2 rounded-lg hover:bg-white transition-colors"
                      >
                        <img
                          src={imageUrl(item.mainImage, "width=120,quality=75,format=auto")}
                          alt={item.title}
                          className="w-14 h-14 rounded-md object-cover shrink-0"
                        />
                        <div>
                          <div className="text-xs font-semibold text-slate-900 group-hover:text-[#004fa4] transition-colors line-clamp-2">
                            {item.title}
                          </div>
                        </div>
                      </Link>
                    ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Quote Section */}
      <QuoteSection />

      {/* Lightbox Modal */}
      <LightboxModal
        images={project.gallery || []}
        currentIndex={lightboxIndex}
        onClose={() => setLightboxIndex(null)}
        onNavigate={(index) => setLightboxIndex(index)}
      />
    </>
  );
}
